import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
/* eslint-disable react-refresh/only-export-components */
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { setActiveCurrency } from "@/lib/currency";
import { authMembershipAction } from "@/lib/auth-membership-hydration";

interface Profile {
  id: string;
  email: string | null;
  full_name: string | null;
}

interface OrgInfo {
  id: string;
  name: string;
  currency?: string | null;
}

interface MembershipRow {
  org_id: string;
  role: string;
  organizations?: { name: string; currency: string | null } | null;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  org: OrgInfo | null;
  orgs: { org: OrgInfo; role: string }[];
  role: string | null;
  loading: boolean;
  switchOrg: (orgId: string) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

const ORG_STORAGE_KEY = "commerceops.active_org_id";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [orgs, setOrgs] = useState<{ org: OrgInfo; role: string }[]>([]);
  const [role, setRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const activeUserIdRef = useRef<string | null>(null);

  const loadProfile = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("profiles")
      .select("id, email, full_name")
      .eq("id", userId)
      .maybeSingle();
    if (data) setProfile(data as Profile);
  }, []);

  const loadOrg = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("memberships")
      .select("org_id, role, organizations(name, currency)")
      .eq("user_id", userId)
      .order("joined_at", { ascending: true });
    if (!data || data.length === 0) {
      setOrg(null);
      setRole(null);
      setOrgs([]);
      return;
    }
    const memberships = data as unknown as MembershipRow[];
    const allOrgs = memberships.map((m) => ({
      org: { id: m.org_id, name: m.organizations?.name ?? "Organization", currency: m.organizations?.currency ?? null },
      role: m.role,
    }));
    setOrgs(allOrgs);

    // Prefer a previously-selected active org; fall back to the first membership.
    const savedOrgId = localStorage.getItem(ORG_STORAGE_KEY);
    const matched = savedOrgId ? allOrgs.find((o) => o.org.id === savedOrgId) : null;
    const active = matched ?? allOrgs[0];
    setOrg(active.org);
    setRole(active.role);
    setActiveCurrency(active.org.currency);
  }, []);

  const hydrateMembership = useCallback(async (userId: string | undefined) => {
    if (!userId) {
      setProfile(null);
      setOrg(null);
      setOrgs([]);
      setRole(null);
      setLoading(false);
      return;
    }
    await Promise.all([loadProfile(userId), loadOrg(userId)]);
    setLoading(false);
  }, [loadProfile, loadOrg]);

  const switchOrg = useCallback((orgId: string) => {
    const found = orgs.find((o) => o.org.id === orgId);
    if (!found) return;
    localStorage.setItem(ORG_STORAGE_KEY, orgId);
    setOrg(found.org);
    setRole(found.role);
    setActiveCurrency(found.org.currency);
    // Force a page reload to clear all cached queries from the previous org.
    window.location.reload();
  }, [orgs]);

  useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const s = data.session;
      const nextUserId = s?.user?.id ?? null;
      activeUserIdRef.current = nextUserId;
      setSession(s);
      setUser(s?.user ?? null);
      void hydrateMembership(nextUserId ?? undefined);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      const previousUserId = activeUserIdRef.current;
      const nextUserId = s?.user?.id ?? null;
      const action = authMembershipAction(event, previousUserId, nextUserId);

      activeUserIdRef.current = nextUserId;
      setSession(s);
      setUser(s?.user ?? null);

      if (action === "none") return;
      if (action === "refresh_profile" && nextUserId) {
        void loadProfile(nextUserId);
        return;
      }
      if (action === "hydrate" && nextUserId) {
        setLoading(true);
        void hydrateMembership(nextUserId);
        return;
      }

      setProfile(null);
      setOrg(null);
      setOrgs([]);
      setRole(null);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [hydrateMembership, loadProfile]);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    activeUserIdRef.current = null;
    setSession(null);
    setUser(null);
    setProfile(null);
    setOrg(null);
    setOrgs([]);
    setRole(null);
    localStorage.removeItem(ORG_STORAGE_KEY);
  }, []);

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const uid = data.session?.user?.id;
    if (uid) {
      await loadProfile(uid);
      await loadOrg(uid);
    }
  }, [loadProfile, loadOrg]);

  return (
    <AuthContext.Provider value={{ session, user, profile, org, orgs, role, loading, switchOrg, signOut, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
